/** Shared password complexity rules (aligned with reset-password / API). */

export const PASSWORD_REQUIREMENTS =
  "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character."

export const PASSWORD_COMPLEXITY =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_\-+=])[A-Za-z\d@$!%*?&#^()_\-+=]{8,}$/

/** Individual live checklist rules (shown one-by-one, not a single blob). */
export const PASSWORD_RULES = [
  {
    id: "length",
    label: "At least 8 characters",
    hint: "Add more characters (8 minimum)",
    test: value => String(value || "").length >= 8,
  },
  {
    id: "upper",
    label: "One uppercase letter (A–Z)",
    hint: "Add an uppercase letter",
    test: value => /[A-Z]/.test(String(value || "")),
  },
  {
    id: "lower",
    label: "One lowercase letter (a–z)",
    hint: "Add a lowercase letter",
    test: value => /[a-z]/.test(String(value || "")),
  },
  {
    id: "number",
    label: "One number (0–9)",
    hint: "Add a number",
    test: value => /\d/.test(String(value || "")),
  },
  {
    id: "special",
    label: "One special character (e.g. @$!%*?&#)",
    hint: "Add a special character",
    test: value => /[@$!%*?&#^()_\-+=]/.test(String(value || "")),
  },
]

export const meetsPasswordRules = value =>
  PASSWORD_COMPLEXITY.test(String(value || ""))

export const getPasswordRuleStatuses = value => {
  const text = String(value || "")
  return PASSWORD_RULES.map(rule => ({
    id: rule.id,
    label: rule.label,
    hint: rule.hint,
    met: rule.test(text),
  }))
}

/**
 * Live helper tone for password fields (standard pattern):
 * - empty → undefined (muted hint only, no red)
 * - typing, invalid → "error"
 * - meets rules → "success"
 */
export const getPasswordHelperTone = value => {
  const text = String(value || "")
  if (!text) return undefined
  return meetsPasswordRules(text) ? "success" : "error"
}

/**
 * Formik field errors that should still show under the password input
 * (requirements are shown via live checklist instead).
 */
export const getPasswordFieldError = (touched, error) => {
  if (!touched || !error) return undefined
  if (error === PASSWORD_REQUIREMENTS) return undefined
  if (error === "Password must be at least 8 characters") return undefined
  if (error === "The password field must be at least 8 characters.") {
    return undefined
  }
  if (error === "New password is required") return undefined
  if (error === "Password is required") return undefined
  if (error === "Please enter your password") return undefined
  return error
}
